import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_TableInstance,
} from "material-react-table";
import CachedIcon from "@mui/icons-material/Cached";
import UploadIcon from "@mui/icons-material/Upload";
import AddIcon from "@mui/icons-material/Add";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { PAGES } from "../../../enums/PagesEnum";
import getAxiosError from "../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import {
  clearCollectiveManifestData,
  updateCollectiveManifestData,
} from "../../../app/features/dedicatedReceiving/storeManifestSlice";
import { CustomTooltip } from "../../../components/common/tooltip/CustomToolTip";
import CommandOpsDataGrid from "../../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import {
  StoreManifestData,
  StoreManifestResponse,
} from "../../../app/models/responseModels/StoreManifestResponse";
import {
  STORE_MANIFEST_COLUMN_NAME,
  STORE_MANIFEST_LABEL_NAME,
  storeManifestViewGridFilter,
  StorePkgExpectedColumns,
} from "../ManifestConstant";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { addDays } from "date-fns";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../../components/common/filter/GridDataFilter";
import agent from "../../../app/api/agent";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../../components/common/modalPopup/useSubmitModal";
import AddNewPackageModal from "./Modals/AddNewPackageModal";
import EditStorePacakgeModal from "./Modals/EditStorePacakgeModal";
import FileUploadModal from "../../../components/common/modalPopup/FileUploadModal";

interface StoreManifestViewProps {
  selectedStoreId: number;
}
const StoreManifestView = ({ selectedStoreId }: StoreManifestViewProps) => {
  const dispatch = useAppDispatch();
  const storePrimaryId = selectedStoreId;
  const { isStoreManifestLoading, collectiveManifestData } = useSelector(
    (state: RootState) => state.storeManifest
  );
  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.USER
    )
  );
  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;
  // state to manage pagination values from grid
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject().setDate(addDays(new Date(), -7)),
    new DateObject().setDate(new Date()),
  ]);
  // ***** States *****
  const [storeManifestRes, setStoreManifestRes] =
    useState<StoreManifestResponse>({
      storeManifestData: [],
      pageSize: 0,
      pageIndex: 0,
      totalStoreManifest: 0,
    });
  const [tableData, setTableData] = useState<StoreManifestData[]>([]);
  const [filterableColumnsState, setFilterableColumnsState] =
    useState<FilterableColumnsState>({});
  const [isTableLoading, setTableLoading] = useState<boolean>(false);

  const getManifestForStore = useCallback(
    async (
      filterableColumns: FilterableColumnsState = filterableColumnsState,
      paginationParam: MRT_PaginationState = pagination
    ) => {
      setTableLoading(true);
      try {
        const response = await agent.StoreManifestAgent.getStoreManifestData({
          storePrimaryId,
          pageIndex: paginationParam.pageIndex,
          pageSize: paginationParam.pageSize,
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD"),
          filters: filterableColumns,
        });
        if (response.isSuccess) {
          setStoreManifestRes(response.data);
          if (Object.keys(filterableColumnsState).length > 0) {
            toast.success("Filter Applied", {
              toastId: "storeManifestData",
            });
          }
        }
        if (!response.isSuccess) {
          toast.error(response.message!, {
            toastId: "storeManifestData",
          });
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "manifestDataListException",
        });
      }
      setTableLoading(false);
    },
    [dispatch, storePrimaryId, pagination, dateRange, filterableColumnsState]
  );

  // ***** UseEffects *****
  useEffect(() => {
    return () => {
      dispatch(clearCollectiveManifestData());
    };
  }, [dispatch]);

  useEffect(() => {
    const key = `page_${pagination.pageIndex}_size_${pagination.pageSize}`;
    const desiredPageData = collectiveManifestData[key] || [];
    if (desiredPageData.length === 0) {
      getManifestForStore();
    } else {
      setTableData(desiredPageData);
    }
  }, [pagination.pageIndex, pagination.pageSize, collectiveManifestData]);

  useEffect(() => {
    const currentPageManifest = storeManifestRes.storeManifestData;
    if (currentPageManifest.length === 0) {
      setTableData([]);
      return;
    }
    const key = `page_${storeManifestRes.pageIndex}_size_${storeManifestRes.pageSize}`;
    dispatch(
      updateCollectiveManifestData({
        key: key,
        currentPageManifest: currentPageManifest,
      })
    );
  }, [storeManifestRes, dispatch]);

  // ***** Handlers *****
  const fetchValuesForSelectedFieldForFilter = (
    columnName: string,
    setClFilterableColumns: React.Dispatch<
      React.SetStateAction<FilterableColumns>
    >
  ) => {
    if (columnName) {
      agent.StoreManifestAgent.fetchValuesByField(
        dateRange[0].format("YYYY-MM-DD"),
        dateRange[1].format("YYYY-MM-DD"),
        columnName,
        storePrimaryId
      )
        .then((res) => {
          let resData = res.data;
          if (res.isSuccess && resData?.length) {
            resData = resData?.map((e) => e.toString());
            setClFilterableColumns((prevState: FilterableColumns) => {
              return {
                ...prevState,
                [columnName]: {
                  ...prevState[columnName],
                  options: resData ?? [],
                },
              };
            });
          }
        })
        .catch((error) => {
          toast.error("Request is failed while loading options.");
        });
    }
  };

  const isDateRangeValid = (): boolean => {
    if (!dateRange) return false;
    if (dateRange.length !== 2) return false;
    const [startDate, endDate] = dateRange;
    return (
      startDate instanceof DateObject &&
      endDate instanceof DateObject &&
      !isNaN(startDate.toDate().getTime()) &&
      !isNaN(endDate.toDate().getTime())
    );
  };

  const handleRefresh = () => {
    dispatch(clearCollectiveManifestData());
  };

  const handleFilterSubmission = () => {
    dispatch(clearCollectiveManifestData());
    setPagination({ pageIndex: 0, pageSize: 50 });
  };

  const handleResetAndRefresh = (
    table: MRT_TableInstance<StoreManifestData>
  ) => {
    table.resetRowSelection(true);
    handleRefresh();
  };

  // ***** Modal Popups *****
  const [addNewPackageModal, openAddPackageModal] = useSubmitModal(
    (closeAddPackageModal) => (
      <AddNewPackageModal
        closeAddPackageModal={closeAddPackageModal}
        onCompleted={() => handleFilterSubmission()}
        storePrimaryId={storePrimaryId}
      />
    )
  );
  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        expectedColumns={StorePkgExpectedColumns}
        asyncSubmitOperation={() => {}}
        onCompleted={handleFilterSubmission}
        sampleFileName={"Bulk_FDPs_Sample.xlsx"}
      />
    )
  );
  const [editPackageModal, openEditPackageModal] =
    useSubmitModalWithParameter<StoreManifestData>(
      (packageRowData, closeEditPackageModal) =>
        IsAdmin ? (
          <EditStorePacakgeModal
            onCompleted={handleFilterSubmission}
            closeEditPackageModal={closeEditPackageModal}
            packageRowData={packageRowData}
          />
        ) : (
          <></>
        )
    );
  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<StoreManifestData>[]>(
    () => [
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.ID,
        header: STORE_MANIFEST_LABEL_NAME.ID,
        size: 10,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.DRIVER_USER_ID,
        header: STORE_MANIFEST_LABEL_NAME.DRIVER_USER_ID,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.TOTE_ID,
        header: STORE_MANIFEST_LABEL_NAME.TOTE_ID,
        size: 30,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.ORDER_ID,
        header: STORE_MANIFEST_LABEL_NAME.ORDER_ID,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.DISPLAY_TRIP_ID,
        header: STORE_MANIFEST_LABEL_NAME.DISPLAY_TRIP_ID,
        size: 40,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.STORE_NUMBER,
        header: STORE_MANIFEST_LABEL_NAME.STORE_NUMBER,
        size: 20,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.PO_NUMBER,
        header: STORE_MANIFEST_LABEL_NAME.PO_NUMBER,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.DROP_NUMBER,
        header: STORE_MANIFEST_LABEL_NAME.DROP_NUMBER,
        size: 10,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.MANIFEST_INGESTED_ON,
        header: STORE_MANIFEST_LABEL_NAME.MANIFEST_INGESTED_ON,
        Cell: ({ cell, row }) =>
          new Date(cell.getValue<string>()).toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.CUSTOMER_ADDRESS_LINE_1,
        header: STORE_MANIFEST_LABEL_NAME.CUSTOMER_ADDRESS_LINE_1,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.CUSTOMER_CITY,
        header: STORE_MANIFEST_LABEL_NAME.CUSTOMER_CITY,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.CUSTOMER_STATE,
        header: STORE_MANIFEST_LABEL_NAME.CUSTOMER_STATE,
      },
      {
        accessorKey: STORE_MANIFEST_COLUMN_NAME.CUSTOMER_POSTALCODE,
        header: STORE_MANIFEST_LABEL_NAME.CUSTOMER_POSTALCODE,
      },
    ],
    []
  );

  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<StoreManifestData>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={() => handleResetAndRefresh(table)}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        {IsCreatePermission && IsAdmin && (
          <CustomTooltip arrow title="Upload/Import">
            <IconButton type="button" onClick={openFileUploadModal}>
              <UploadIcon sx={{ color: "primary.main" }} />
            </IconButton>
          </CustomTooltip>
        )}
        {IsCreatePermission && IsAdmin && (
          <CustomTooltip arrow title="Add New">
            <span>
              <IconButton
                disabled={isStoreManifestLoading}
                type="button"
                onClick={openAddPackageModal}
              >
                <AddIcon sx={{ color: "primary.main" }} />
              </IconButton>
            </span>
          </CustomTooltip>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ width: "100%", mb: -3, mt: -2 }}>
        <Grid container alignItems="center">
          <Box zIndex={100} style={{ overflowY: "auto" }}>
            <DatePicker
              format="MM/DD/YYYY"
              className="green"
              dateSeparator=" ~ "
              range
              rangeHover
              value={dateRange}
              onChange={(dateObjects: DateObject[]) => {
                setDateRange(dateObjects);
              }}
              maxDate={new Date()}
            />
          </Box>
          <Button
            disabled={!isDateRangeValid()}
            size="large"
            onClick={handleRefresh}
          >
            SUBMIT
          </Button>
        </Grid>
        <Grid item xs={12} mb={2}>
          <GridDataFilter
            title="Search Store Manifest"
            filterableColumns={storeManifestViewGridFilter}
            asyncSelectedColumnValues={fetchValuesForSelectedFieldForFilter}
            asyncSubmitFunction={(filterState: FilterableColumnsState) => {
              setFilterableColumnsState(filterState);
              handleFilterSubmission();
              if (Object.keys(filterState).length === 0) {
                toast.success("Filter Removed", {
                  toastId: "storeManifestData",
                });
              }
            }}
          />
        </Grid>
        <CommandOpsDataGrid
          data={tableData}
          columns={columns}
          isLoading={isStoreManifestLoading || isTableLoading}
          renderTopToolbarCustomActions={renderTopToolbarCustomActions}
          emptyRowMessage={"No Manifest"}
          rowCount={storeManifestRes.totalStoreManifest.toLocaleString()}
          openModal={openEditPackageModal}
          isDescending={false}
          defaultSortColumnName="id"
          manualPagination={true}
          pagination={pagination}
          handlePagination={(value: MRT_PaginationState) => {
            setPagination(value);
          }}
        />
        {addNewPackageModal}
        {editPackageModal}
        {fileUploadModal}
      </Box>
    </>
  );
};

export default StoreManifestView;
